import { Injectable, Injector, NgZone } from '@angular/core';
import { environment } from 'environments/environment';
import { RootEntityContextService } from 'app/shared/root-entity-context.service';

/**
 * Optional background prefetch of Collection framework trees, gated entirely by
 * `environment.collectionDataPrefetchEnabled`.
 *
 * When a user lands on a root entity this walks the first few child entities and pulls their
 * framework trees from S3 into the cache Collection already reads. The tree carries the STATIC
 * questionnaire structure, so a framework opened later renders its cards straight from cache.
 * Dynamic per-question data (answers, comments, logs, settings, assignments, reviewers) is
 * deliberately NOT prefetched — it keeps its on-demand flow and its section skeletons.
 *
 * Nothing here is awaited by the screen that triggers it: the walk is started and forgotten, runs
 * outside Angular's zone so its background resolutions don't churn change detection, and each tree
 * lands in the cache the moment it arrives, wherever the user happens to be by then.
 *
 * When the flag is OFF this does nothing at all: no API calls, no cache writes, and not even a
 * RootEntityContextService instantiation (it is resolved lazily from the Injector inside the run).
 */
@Injectable({ providedIn: 'root' })
export class CollectionPrefetchService {
  // client-agreed ceiling: never warm more than this many child entities
  private static readonly MAX_CHILD_ENTITIES = 5;

  // one attempt per root entity per app load
  private readonly startedRootEntityIds = new Set<string>();
  // set when the user reaches Collection: the walk stops so it never competes with that screen
  private cancelled = false;

  constructor(
    private injector: Injector,
    private ngZone: NgZone
  ) {}

  /**
   * Single entry point, called on every completed navigation. Starts a prefetch the first time a
   * root entity is entered away from Collection, and stands down as soon as Collection is opened.
   * Returns immediately — the caller never waits on prefetching.
   */
  onNavigation(url: string): void {
    if (!environment.collectionDataPrefetchEnabled) {
      return;
    }
    const path = (url || '').split(/[?#]/)[0];
    const segments = path.split('/').filter(Boolean);
    if (segments[0] !== 'first-party') {
      return;
    }
    // if (segments.includes('collection')) {
    //   // the user is on the screen we were warming — its own flow owns the data from here
    //   this.cancelled = true;
    //   return;
    // }
    const rootEntityId = segments[1];
    if (!rootEntityId || this.startedRootEntityIds.has(rootEntityId)) {
      return;
    }
    this.startedRootEntityIds.add(rootEntityId);
    this.cancelled = false;
    // fire and forget, outside the zone: the root entity screen carries on untouched
    this.ngZone.runOutsideAngular(() => void this.prefetchRootEntity(rootEntityId));
  }

  /** Warms the first MAX_CHILD_ENTITIES child entities, one after another, never in parallel. */
  private async prefetchRootEntity(rootEntityId: string): Promise<void> {
    try {
      const recs = this.injector.get(RootEntityContextService);
      const subEntities = (await recs.getSubEntities('CHILD_ENTITY', rootEntityId)) || [];
      // alphabetical, the order the Collection sub-entity dropdown itself uses (sortSubEntities)
      const relevant = subEntities
        .filter(sub => sub?.activeAssessmentId)
        .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
        .slice(0, CollectionPrefetchService.MAX_CHILD_ENTITIES);

      for (const subEntity of relevant) {
        if (this.cancelled) {
          return;
        }
        await this.prefetchFrameworkTrees(recs, subEntity.activeAssessmentId);
      }
    } catch {
      // background work: a failure just leaves the on-demand flow to fetch as it does today
    }
  }

  /**
   * Warms one assessment's framework trees. Both calls are the ones opening that framework would
   * make anyway, and both short-circuit on the existing cache: getAssessmentTree() returns straight
   * away when the tree is already loaded, and joins Collection's own request when one is in flight.
   */
  private async prefetchFrameworkTrees(recs: RootEntityContextService, assessmentId: string): Promise<void> {
    try {
      const frameworks = (await recs.getFrameworksByAssessmentId(assessmentId, null, false)) || [];
      for (const framework of frameworks) {
        if (this.cancelled) {
          return;
        }
        if (!framework?.key) {
          continue;
        }
        // resolves from cache when already loaded; each tree is stored as soon as it arrives
        await recs.getAssessmentTree(assessmentId, framework);
      }
    } catch {
      // same as above - silent, the on-demand flow remains the fallback
    }
  }
}
